import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { RosterChangeTransition } from "@/pages/roster-view/components/RosterReissueSummary";
import {
  versionChangesBaseLabel,
  type VersionChanges,
} from "@/services/roster/versionChanges";
import { useVersionChanges } from "../hook/useVersionChanges";

export function VersionChangesSummary({
  data,
}: {
  readonly data: VersionChanges;
}) {
  return (
    <span className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
      <span>바뀐 칸 {data.summary.changed_cells}</span>
      <span>바뀐 사람 {data.summary.affected_nurses}</span>
      <span>추가 {data.summary.added_nurses}명</span>
      <span>제외 {data.summary.removed_nurses}명</span>
    </span>
  );
}

export function RosterVersionChangesDialog({
  scheduleId,
  groupId,
  onClose,
}: {
  readonly scheduleId: string;
  readonly groupId: string;
  readonly onClose: () => void;
}) {
  const query = useVersionChanges(groupId, scheduleId);
  const data = query.data;
  const groups = new Map<
    string,
    { name: string; cells: VersionChanges["cells"] }
  >();
  for (const cell of data?.cells ?? []) {
    const group = groups.get(cell.nurse_id) ?? {
      name: cell.nurse_name || cell.nurse_id,
      cells: [],
    };
    group.cells.push(cell);
    groups.set(cell.nurse_id, group);
  }
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        className="flex max-h-[85dvh] w-[calc(100vw-32px)] max-w-[640px] flex-col gap-4 rounded-2xl p-6 font-pretendard"
        data-testid="roster-version-changes-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            변경 내역{data ? ` · VER${data.schedule.version}` : ""}
          </DialogTitle>
          <DialogDescription>
            {data
              ? versionChangesBaseLabel(data)
              : "저장된 근무표의 변경 내역을 확인합니다."}
          </DialogDescription>
        </DialogHeader>
        {query.isPending ? (
          <div
            role="status"
            aria-label="변경 내역 불러오는 중"
            className="space-y-3"
          >
            <Skeleton className="h-6 w-3/4" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : query.isError ? (
          <div role="alert">
            <p className="mb-2 text-sm">변경 내역을 불러오지 못했습니다.</p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => void query.refetch()}
            >
              다시 시도
            </Button>
          </div>
        ) : (
          data && (
            <>
              <VersionChangesSummary data={data} />
              <div className="min-h-0 overflow-y-auto overscroll-contain">
                {groups.size === 0 && (
                  <p className="py-6 text-center text-sm text-muted-foreground">
                    {data.base?.kind === "source_missing" || data.base === null
                      ? "비교할 수 있는 기준이 없습니다."
                      : "변경된 근무가 없습니다."}
                  </p>
                )}
                {[...groups].map(([id, group]) => (
                  <section
                    key={id}
                    className="border-t border-border py-3 first:border-t-0"
                  >
                    <h3 className="mb-2 flex flex-wrap items-center gap-2 text-sm font-semibold">
                      {group.name}
                      <span className="ml-auto text-xs text-orange-600">
                        {group.cells.length}칸
                      </span>
                    </h3>
                    <div className="grid grid-cols-2 gap-2">
                      {group.cells.map((cell) => (
                        <div
                          key={cell.date}
                          className="flex flex-col gap-1 rounded-lg bg-light-muted px-3 py-2 text-xs"
                        >
                          <span>
                            {Number(cell.date.slice(-2))}일 (
                            {new Date(
                              `${cell.date}T12:00:00`
                            ).toLocaleDateString("ko-KR", { weekday: "short" })}
                            )
                          </span>
                          <RosterChangeTransition cell={cell} tone="version" />
                          {cell.kind !== "cell" && (
                            <span className="text-muted-foreground">
                              {cell.kind === "added"
                                ? "새로 추가"
                                : "근무 전부 삭제"}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            </>
          )
        )}
      </DialogContent>
    </Dialog>
  );
}
